class ProductItem extends HTMLElement {
  constructor() {
    super();

    this.imageSelector =
      '.product-item__bg[data-product-image-default]';

    this.buttonSelector =
      '.btn-add-to-cart';

    this.lightThreshold = 180;
    this.edgeSize = 8;

    this.currentImageUrl = null;
    this.observer = null;
    this.intersectionObserver = null;
  }

  connectedCallback() {
    this.setupIntersectionObserver();
    this.setupMutationObserver();
    this.bindSwatches();
    this.bindAddToCart();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  setupIntersectionObserver() {
    this.intersectionObserver =
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            this.detectBackgroundColor();
          });
        },
        {
          rootMargin: '300px 0px',
          threshold: 0
        }
      );

    this.intersectionObserver.observe(this);
  }

  setupMutationObserver() {
    this.observer =
      new MutationObserver(
        (mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'style'
            ) {
              if (
                mutation.target.matches(
                  this.imageSelector
                )
              ) {
                this.detectBackgroundColor();
              }
            }

            if (
              mutation.type === 'childList' &&
              mutation.addedNodes.length
            ) {
              this.detectBackgroundColor();
            }
          });
        }
      );

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: true
    });
  }

  getBackgroundUrl(element) {
    const backgroundImage =
      getComputedStyle(element)
        .backgroundImage;

    if (
      !backgroundImage ||
      backgroundImage === 'none'
    ) {
      return null;
    }

    const match =
      backgroundImage.match(
        /url\(["']?(.*?)["']?\)/
      );

    const url =
      match ? match[1] : null;

    return url;
  }

  detectBackgroundColor() {
    const imageElement =
      this.querySelector(
        this.imageSelector
      );

    const button =
      this.querySelector(
        this.buttonSelector
      );

    if (
      !imageElement ||
      !button
    ) {
      return;
    }

    const imageUrl =
      this.getBackgroundUrl(
        imageElement
      );

    if (!imageUrl) {
      return;
    }

    if (
      this.currentImageUrl === imageUrl
    ) {
      return;
    }

    this.currentImageUrl = imageUrl;

    this.analyzeImage(
      imageUrl,
      button
    );
  }

  analyzeImage(imageUrl, button) {
    const image = new Image();

    image.onload = () => {
      const size = 80;

      const canvas =
        document.createElement('canvas');

      canvas.width = size;
      canvas.height = size;

      const ctx =
        canvas.getContext(
          '2d',
          {
            willReadFrequently: true
          }
        );

      if (!ctx) {
        return;
      }

      ctx.drawImage(
        image,
        0,
        0,
        size,
        size
      );

      let imageData;

      try {
        imageData =
          ctx.getImageData(
            0,
            0,
            size,
            size
          );
      } catch (error) {
        console.error(
          '[ProductItem] Cannot read pixels:',
          error
        );

        return;
      }

      const pixels =
        imageData.data;

      let lightPixels = 0;
      let darkPixels = 0;

      for (
        let y = 0;
        y < this.edgeSize;
        y++
      ) {
        for (
          let x = 0;
          x < size;
          x++
        ) {
          const top =
            this.getPixelType(
              pixels,
              x,
              y,
              size
            );

          const bottom =
            this.getPixelType(
              pixels,
              x,
              size - 1 - y,
              size
            );

          if (top === 'light') {
            lightPixels++;
          }

          if (top === 'dark') {
            darkPixels++;
          }

          if (bottom === 'light') {
            lightPixels++;
          }

          if (bottom === 'dark') {
            darkPixels++;
          }
        }
      }

      for (
        let y = this.edgeSize;
        y < size - this.edgeSize;
        y++
      ) {
        for (
          let x = 0;
          x < this.edgeSize;
          x++
        ) {
          const left =
            this.getPixelType(
              pixels,
              x,
              y,
              size
            );

          const right =
            this.getPixelType(
              pixels,
              size - 1 - x,
              y,
              size
            );

          if (left === 'light') {
            lightPixels++;
          }

          if (left === 'dark') {
            darkPixels++;
          }

          if (right === 'light') {
            lightPixels++;
          }

          if (right === 'dark') {
            darkPixels++;
          }
        }
      }

      const isLight =
        lightPixels >= darkPixels;

      button.classList.toggle(
        'is-light',
        isLight
      );

      button.classList.toggle(
        'is-dark',
        !isLight
      );
    };

    image.onerror = (error) => {
      console.error(
        '[ProductItem] image load failed:',
        imageUrl,
        error
      );
    };

    image.src = imageUrl;
  }

  getPixelType(
    pixels,
    x,
    y,
    size
  ) {
    const index =
      (y * size + x) * 4;

    const r =
      pixels[index];

    const g =
      pixels[index + 1];

    const b =
      pixels[index + 2];

    const alpha =
      pixels[index + 3];

    if (alpha < 200) {
      return null;
    }

    const luminance =
      0.299 * r +
      0.587 * g +
      0.114 * b;

    return luminance >
      this.lightThreshold
      ? 'light'
      : 'dark';
  }

  bindSwatches() {
    this.addEventListener(
      'click',
      (event) => {
        const swatch =
          event.target.closest(
            '[data-product-color-swatch]'
          );

        if (!swatch) {
          return;
        }

        this.currentImageUrl = null;

        setTimeout(() => {
          this.detectBackgroundColor();
        }, 100);

        setTimeout(() => {
          this.detectBackgroundColor();
        }, 300);
      }
    );
  }

  bindAddToCart() {
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(
          '.product-information .btn-add-to-cart'
        );

        if (
          !button ||
          !this.contains(button)
        ) {
          return;
        }

        event.preventDefault();

        const clickedButtons =
          document.querySelectorAll(
            '.product-information .btn-add-to-cart.clicked'
          );

        clickedButtons.forEach((el) => {
          if (el !== button) {
            el.classList.remove('clicked');
          }
        });

        button.classList.toggle('clicked');
      }
    );

    if (!ProductItem.__addToCartOutsideClickBound) {
      ProductItem.__addToCartOutsideClickBound = true;

      document.addEventListener(
        'click',
        (event) => {
          const target = event.target;

          const button =
            target &&
            target.closest &&
            target.closest(
              '.product-information .btn-add-to-cart'
            );

          const swatches =
            target &&
            target.closest &&
            target.closest(
              '.floating-swatches-wrapper'
            );

          if (button || swatches) {
            return;
          }

          const clickedButtons =
            document.querySelectorAll(
              '.product-information .btn-add-to-cart.clicked'
            );

          clickedButtons.forEach((el) => {
            el.classList.remove('clicked');
          });
        },
        true
      );
    }
  }
}

if (
  !customElements.get('product-item')
) {
  customElements.define(
    'product-item',
    ProductItem
  );
}


/* ============================================
   Swiper mobile swatch touch fix
   ============================================ */

var MOBILE_BREAKPOINT = 767;

var SWATCH_SELECTOR =
  '.floating-swatches-wrapper';

function isMobile() {
  return window.innerWidth <=
    MOBILE_BREAKPOINT;
}

function isInsideSwatches(target) {
  return (
    target &&
    target.closest &&
    target.closest(SWATCH_SELECTOR)
  );
}

function initSwiper(swiper) {
  if (
    !swiper ||
    !swiper.el ||
    swiper.el.__mobileSwatchTouchFix
  ) {
    return;
  }

  swiper.el.__mobileSwatchTouchFix =
    true;

  var el = swiper.el;

  el.addEventListener(
    'touchstart',
    function (event) {
      if (!isMobile()) return;

      if (
        !isInsideSwatches(
          event.target
        )
      ) {
        return;
      }

      el.__swatchTouchActive =
        true;

      swiper.allowTouchMove = false;
    },
    true
  );

  el.addEventListener(
    'touchmove',
    function (event) {
      if (!isMobile()) return;

      if (!el.__swatchTouchActive) {
        return;
      }

      swiper.allowTouchMove = false;
    },
    true
  );

  el.addEventListener(
    'touchend',
    function () {
      if (!el.__swatchTouchActive) {
        return;
      }

      el.__swatchTouchActive = false;

      if (isMobile()) {
        swiper.allowTouchMove = true;
      }
    },
    true
  );

  el.addEventListener(
    'touchcancel',
    function () {
      if (!el.__swatchTouchActive) {
        return;
      }

      el.__swatchTouchActive = false;

      if (isMobile()) {
        swiper.allowTouchMove = true;
      }
    },
    true
  );
}

function init() {
  if (!isMobile()) {
    return;
  }

  document
    .querySelectorAll('.swiper')
    .forEach(function (el) {
      if (el.swiper) {
        initSwiper(el.swiper);
      }
    });
}

// Initial
init();

// Swiper/product recommendations may render asynchronously
var observer =
  new MutationObserver(
    function () {
      init();
    }
  );

observer.observe(
  document.body,
  {
    childList: true,
    subtree: true
  }
);